import { useState, Fragment } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import ImportAgendaModal from 'pages/Modals/ImportAgendaModal'
import AlertDialog from 'components/AlertDialog'
import professionalWorkingDaysValidator from 'validators/professionalWorkingDaysValidator'
import { FaFileImport } from 'react-icons/fa'
import { Formik } from 'formik'
import { DateTime } from 'luxon'

import {
  Form,
  SubTitle,
  FormField,
  Label,
  WarningText,
  ErrorContainer,
  Row
} from '../styles'

export default function AgendaForm ({
  professionalData = {},
  businessWorkingDays = [],
  businessTimeWindows = [],
  onlineTimeWindows = [],
  cutileiTimeWindows = [],
  professionals = []
}) {
  const [workStartTimeWindows, setWorkStartTimeWindows] = useState (businessTimeWindows)
  const [workEndTimeWindows, setWorkEndTimeWindows] = useState (businessTimeWindows)
  const [onlineStartTimeWindows, setOnlineStartTimeWindows] = useState (onlineTimeWindows)
  const [onlineEndTimeWindows, setOnlineEndTimeWindows] = useState (onlineTimeWindows)
  const [cutileiStartTimeWindows, setCutileiStartTimeWindows] = useState (cutileiTimeWindows)
  const [cutileiEndTimeWindows, setCutileiEndTimeWindows] = useState (cutileiTimeWindows)
  const [createdProfessional, setCreatedProfessional] = useState (null)
  const [showImportAgendaModal, setShowImportAgendaModal] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  const navigate = useNavigate ()
  const location = useLocation ()
  const isTutorial = !!location.state?.isTutorial
  const isCutileiEligible = professionalData.service_categories.some (c => (
    c.name.toLowerCase () === 'manicure e pedicure'
  ))

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const handleProfessionalAdded = () => {
    navigate (
      isTutorial ? '/business' : `/business/professionals/${createdProfessional?.id}/edit`,
      { replace: true }
    )
  }

  const toggleImportAgendaModal = () => setShowImportAgendaModal (!showImportAgendaModal)
  const toggleOkDialog = () => setShowOkDialog (!showOkDialog)

  return (
    <Formik
      validationSchema={professionalWorkingDaysValidator}
      initialValues={{
        working_days: professionalData.working_days.map (day => ({
          ...day,
          accepts_cutilei: day.accepts_cutilei && isCutileiEligible
        }))
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setErrorMessage (null)
        setSubmitting (true)

        const { order, birth, gender, access_level, service_categories } = professionalData

        try {
          const services = professionalData.services.map (service => ({
            ...service, comission_percentage: service.comission_percentage / 100
          }))

          const working_days = values.working_days.filter (day => day.selected).map (day => ({
            ...day,
            work_start: day.work_start.value,
            work_end: day.work_end.value,
            online_start: day.online_start.value,
            online_end: day.online_end.value,
            cutilei_start: day.cutilei_start.value,
            cutilei_end: day.cutilei_end.value
          }))

          const { data: professional } = await cutileiApi.post (
            `/businesses/${businessId}/professionals`, {
              ...professionalData,
              number: order,
              birth: DateTime.fromFormat (birth, 'ddMMyyyy').toISODate (),
              gender: gender.value,
              access_level_id: access_level.value.id
            }, requestConfig
          )

          if (access_level.value.codename === 'service') {
            await cutileiApi.post (
              `/businesses/${businessId}/professionals/${professional.id}/working_days`,
              {working_days},
              requestConfig
            )

            await cutileiApi.post (
              `/businesses/${businessId}/professionals/${professional.id}/service_categories`,
              {service_categories},
              requestConfig
            )

            await cutileiApi.post (
              `/businesses/${businessId}/professionals/${professional.id}/services`,
              {services},
              requestConfig
            )
          }

          setSubmitting (false)
          setCreatedProfessional (professional)
          toggleOkDialog ()
        } catch (error) {
          setSubmitting (false)
          setErrorMessage (getApiErrorMessage (error))
          logApiError (error)
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        setTouched,
        handleSubmit
      }) => {
        const handleTimeSelected = (name, option) => {
          const dayIndex = parseInt (name.split ('.')[1])
          const businessClosing = DateTime.fromISO (businessWorkingDays[dayIndex].closing).toFormat ('H:mm')
          const businessOpening = DateTime.fromISO (businessWorkingDays[dayIndex].opening).toFormat ('H:mm')

          if (name.includes ('work_start')) {
            setWorkEndTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (option.value, businessClosing, true)
            }))
            setOnlineStartTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                option.value, values.working_days[dayIndex].work_end.value || businessClosing
              )
            }))
            setCutileiStartTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                option.value, values.working_days[dayIndex].work_end.value || businessClosing
              )
            }))
          } else if (name.includes ('work_end')) {
            setWorkStartTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (businessOpening, option.value, true)
            }))
            setOnlineEndTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                values.working_days[dayIndex].work_start.value || businessOpening, option.value
              )
            }))
            setCutileiEndTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                values.working_days[dayIndex].work_start.value || businessOpening, option.value
              )
            }))
          }

          setFieldValue ('working_days', values.working_days.map ((day, index) => ({
            ...day,
            work_start: name.includes ('work_start') && !day.work_start
              ? option : index === dayIndex && name.includes ('work_start')
                ? option : day.work_start,
            work_end: name.includes ('work_end') && !day.work_end
              ? option : index === dayIndex && name.includes ('work_end')
                ? option : day.work_end
          })))
        }

        const handleOnlineTimeSelected = (name, option) => {
          const dayIndex = parseInt (name.split ('.')[1])
          const lastTimeWindowIndex = onlineEndTimeWindows[dayIndex].length -1

          if (name.includes ('online_opening')) {
            setOnlineEndTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                option.value, onlineEndTimeWindows[dayIndex][lastTimeWindowIndex].value, true
              )
            }))
          } else if (name.includes ('online_closing')) {
            setOnlineStartTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                onlineStartTimeWindows[dayIndex][0].value, option.value, true
              )
            }))
          }

          setFieldValue ('working_days', values.working_days.map ((day, index) => ({
            ...day,
            online_start: name.includes ('online_start') && day.online_start === ''
              ? option : index === dayIndex && name.includes ('online_start')
                ? option : day.online_start,
            online_end: name.includes ('online_end') && day.online_end === ''
              ? option : index === dayIndex && name.includes ('online_end')
                ? option : day.online_end
          })))
        }

        const handleCutileiTimeSelected = (name, option) => {
          const dayIndex = parseInt (name.split ('.')[1])
          const lastTimeWindowIndex = cutileiEndTimeWindows[dayIndex].length -1

          if (name.includes ('cutilei_opening')) {
            setCutileiEndTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                option.value, cutileiEndTimeWindows[dayIndex][lastTimeWindowIndex].value, true
              )
            }))
          } else if (name.includes ('cutilei_closing')) {
            setCutileiStartTimeWindows (prev => ({
              ...prev,
              [dayIndex]: TimeWindow.getTimeWindows (
                cutileiStartTimeWindows[dayIndex][0].value, option.value, true
              )
            }))
          }

          setFieldValue ('working_days', values.working_days.map ((day, index) => ({
            ...day,
            cutilei_start: name.includes ('cutilei_start') && day.cutilei_start === ''
              ? option : index === dayIndex && name.includes ('cutilei_start')
                ? option : day.cutilei_start,
            cutilei_end: name.includes ('cutilei_end') && day.cutilei_end === ''
              ? option : index === dayIndex && name.includes ('cutilei_end')
                ? option : day.cutilei_end
          })))
        }

        return (
          <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
            <SubTitle>Disponibilidade de atendimento da agenda do profissional</SubTitle>
            {professionals.length > 0 && (
              <SecondaryButton
                label='Importar configurações'
                leftIcon={<FaFileImport size={12} color='#FFC100' style={{marginBottom: 1}}/>}
                onClick={toggleImportAgendaModal}
                color='#606060'
                width={200}
                style={{marginBottom: 15}}
              />
            )}
            <Row style={{alignItems: 'flex-start'}}>
              {values.working_days.map ((day, index) => (
                <FormField key={day.id}>
                  <Row style={{marginBottom: -5, alignItems: 'center'}}>
                    <CheckBox
                      id={`working_days.${index}.selected`}
                      style={{marginRight: 6}}
                      value={day.selected}
                      onValueChange={value => {
                        setFieldValue (`working_days.${index}`, {
                          ...values.working_days[index],
                          ...value === true && {
                              work_start: '',
                              work_end: '',
                              online_start: '',
                              online_end: '',
                              cutilei_start: '',
                              cutilei_end: '',
                              accepts_cutilei: false
                            },
                            selected: value
                          })
              
                        if (value === true) setTouched ({
                          [`working_days.${index}.work_start`]: false,
                          [`working_days.${index}.work_end`]: false,
                          [`working_days.${index}.online_start`]: false,
                          [`working_days.${index}.online_end`]: false,
                          [`working_days.${index}.cutilei_start`]: false,
                          [`working_days.${index}.cutilei_end`]: false,
                          [`working_days.${index}.accepts_online`]: false,
                          [`working_days.${index}.accepts_cutilei`]: false
                        })
                      }}
                    />
                    <Label htmlFor={`working_days.${index}.selected`}>
                      {day.name}
                    </Label>
                  </Row>
                  {day.selected && (
                    <Fragment>
                      <Select
                        name={`working_days.${index}.work_start`}
                        placeholder='Início...'
                        value={day.work_start}
                        options={workStartTimeWindows[index]}
                        onChange={handleTimeSelected}
                        onBlur={setFieldTouched}
                        containerStyles={{marginTop: '10px'}}
                        error={errors.working_days?.[index]}
                        touched={touched.working_days?.[index]}
                      />
                      {touched.working_days?.[index]?.work_start && errors.working_days?.[index]?.work_start && (
                        <WarningText>{errors.working_days[index].work_start}</WarningText>
                      )}
                      <Select
                        name={`working_days.${index}.work_end`}
                        placeholder='Término...'
                        value={day.work_end}
                        options={workEndTimeWindows[index]}
                        onChange={handleTimeSelected}
                        onBlur={setFieldTouched}
                        containerStyles={{marginTop: '10px'}}
                        error={errors.working_days?.[index]}
                        touched={touched.working_days?.[index]}
                      />
                      {touched.working_days?.[index]?.work_end && errors.working_days?.[index]?.work_end && (
                        <WarningText>{errors.working_days[index].work_end}</WarningText>
                      )}
                    </Fragment>
                  )}
                </FormField>
              ))}
            </Row>
            {isCutileiEligible && (
              <Fragment>
                <SubTitle>Disponibilidade de atendimento para a Cutilei</SubTitle>
                <Row>
                  {values.working_days.map ((day, index) => (
                    day.selected && businessWorkingDays[index].accepts_cutilei ? (
                      <FormField key={index}>
                        <Row style={{marginTop: 6, marginBottom: -5}}>
                          <CheckBox
                            id={`working_days.${index}.accepts_cutilei`}
                            style={{marginTop: 3, marginRight: 6}}
                            value={day.accepts_cutilei}
                            onValueChange={value => {
                              if (value === false) {
                                setFieldValue (`working_days.${index}.cutilei_start`, '')
                                setFieldValue (`working_days.${index}.cutilei_end`, '')
                                setFieldTouched (`working_days.${index}.cutilei_start`, false)
                                setFieldTouched (`working_days.${index}.cutilei_end`, false)
                              }
                              setFieldValue (`working_days.${index}.accepts_cutilei`, value)
                            }}
                          />
                          <Label htmlFor={`working_days.${index}.accepts_cutilei`}>
                            {day.name}
                          </Label>
                        </Row>
                        {day.accepts_cutilei && (
                          <Fragment>
                            <Select
                              name={`working_days.${index}.cutilei_start`}
                              placeholder='Abertura...'
                              value={day.cutilei_start}
                              options={cutileiStartTimeWindows[index]}
                              onChange={handleCutileiTimeSelected}
                              onBlur={setFieldTouched}
                              containerStyles={{marginTop: '10px'}}
                              error={errors.working_days?.[index]}
                              touched={touched.working_days?.[index]}
                            />
                            {touched.working_days?.[index]?.cutilei_start && errors.working_days?.[index]?.cutilei_start && (
                              <WarningText>{errors.working_days[index].cutilei_start}</WarningText>
                            )}
                            <Select
                              name={`working_days.${index}.cutilei_end`}
                              placeholder='Fechamento...'
                              value={day.cutilei_end}
                              options={cutileiEndTimeWindows[index]}
                              onChange={handleCutileiTimeSelected}
                              onBlur={setFieldTouched}
                              containerStyles={{marginTop: '10px'}}
                              error={errors.working_days?.[index]}
                              touched={touched.working_days?.[index]}
                            />
                            {touched.working_days?.[index]?.cutilei_end && errors.working_days?.[index]?.cutilei_end && (
                              <WarningText>{errors.working_days[index].cutilei_end}</WarningText>
                            )}
                          </Fragment>
                        )}
                      </FormField>
                    ) : (
                      <FormField key={index}/>
                    )
                  ))}
                </Row>
              </Fragment>
            )}
            <SubTitle>Disponibilidade de atendimento para agendamentos online</SubTitle>
            <Row style={{alignItems: 'flex-start'}}>
              {values.working_days.map ((day, index) => (
                day.selected && businessWorkingDays[index].accepts_online ? (
                  <FormField key={day.id}>
                    <Row style={{marginBottom: -5, alignItems: 'center'}}>
                      <CheckBox
                        id={`working_days.${index}.accepts_online`}
                        style={{marginTop: 3, marginRight: 6}}
                        value={day.accepts_online}
                        onValueChange={value => {
                          if (value === false) {
                            setFieldValue (`working_days.${index}.online_start`, '')
                            setFieldValue (`working_days.${index}.online_end`, '')
                            setFieldTouched (`working_days.${index}.online_start`, false)
                            setFieldTouched (`working_days.${index}.online_end`, false)
                          }
                          setFieldValue (`working_days.${index}.accepts_online`, value)
                        }}
                      />
                      <Label htmlFor={`working_days.${index}.accepts_online`}>
                        {day.name}
                      </Label>
                    </Row>
                    {businessWorkingDays[index].accepts_online && day.accepts_online && (
                      <Fragment>
                        <Select
                          name={`working_days.${index}.online_start`}
                          placeholder='Início...'
                          value={day.online_start}
                          options={workStartTimeWindows[index]}
                          onChange={handleOnlineTimeSelected}
                          onBlur={setFieldTouched}
                          containerStyles={{marginTop: '10px'}}
                          error={errors.working_days?.[index]}
                          touched={touched.working_days?.[index]}
                        />
                        {touched.working_days?.[index]?.online_start && errors.working_days?.[index]?.online_start && (
                          <WarningText>{errors.working_days[index].online_start}</WarningText>
                        )}
                        <Select
                          name={`working_days.${index}.online_end`}
                          placeholder='Término...'
                          value={day.online_end}
                          options={workEndTimeWindows[index]}
                          onChange={handleOnlineTimeSelected}
                          onBlur={setFieldTouched}
                          containerStyles={{marginTop: '10px'}}
                          error={errors.working_days?.[index]}
                          touched={touched.working_days?.[index]}
                        />
                        {touched.working_days?.[index]?.online_end && errors.working_days?.[index]?.online_end && (
                          <WarningText>{errors.working_days[index].online_end}</WarningText>
                        )}
                      </Fragment>
                    )}
                  </FormField>
                ) : (
                  <FormField key={index}/>
                )
              ))}
            </Row>
            <ImportAgendaModal
              visible={showImportAgendaModal}
              professionals={professionals}
              onConfirm={workingDays => {
                setFieldValue ('working_days', businessWorkingDays.map (day => {
                  const workingDay = workingDays.find (workingDay => workingDay.id === day.id)

                  if (workingDay) {
                    const work_start = TimeWindow.toShortTime (workingDay.work_start)
                    const work_end = TimeWindow.toShortTime (workingDay.work_end)
                    const online_start = TimeWindow.toShortTime (workingDay.online_start || workingDay.work_start)
                    const online_end = TimeWindow.toShortTime (workingDay.online_end || workingDay.work_end)
                    const cutilei_start = TimeWindow.toShortTime (workingDay.cutilei_start || workingDay.work_start)
                    const cutilei_end = TimeWindow.toShortTime (workingDay.cutilei_end || workingDay.work_end)

                    return { 
                      ...workingDay,
                      work_start: { label: work_start, value: work_start },
                      work_end: { label: work_end, value: work_end },
                      online_start: { label: online_start, value: online_start },
                      online_end: { label: online_end, value: online_end },
                      cutilei_start: { label: cutilei_start, value: cutilei_start },
                      cutilei_end: { label: cutilei_end, value: cutilei_end },
                      selected: true
                    } 
                  } else return {
                    ...day, work_start: '', work_end: '', accepts_online: false, accepts_cutilei: false,
                    online_start: '', online_end: '', cutilei_start: '', cutilei_end: '', selected: false
                  }
                }))
              }}
              onClose={toggleImportAgendaModal}
            />
            {errorMessage && (
              <ErrorContainer>
                <p>{errorMessage}</p>
              </ErrorContainer>
            )}
            <AlertDialog
              visible={showOkDialog && createdProfessional !== null}
              title='Sucesso'
              message='Profissional cadastrado!'
              confirmText='Ok'
              onConfirm={handleProfessionalAdded}
              onClose={handleProfessionalAdded}
            />
            <MainButton
              label='Finalizar cadastro'
              onClick={handleSubmit}
              disabled={isSubmitting}
              loading={isSubmitting}
              width={160}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
